/** The settled location, which is not always the current one. */
import { useState } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * TanStack reports the *pending* location for the whole of a navigation, where
 * Next only published it once the route change completed. Reading TanStack
 * directly would therefore move sidebar selection and `next=` capture to the
 * destination before the user arrives (ANO-079).
 *
 * A shallow change — same path, new params — did commit immediately under Next,
 * so that case adopts the pending location straight away.
 *
 * Not `RouterState.resolvedLocation`: `undefined` until the first navigation
 * settles, a commit behind (layout-effect assigned, so `next=` captures the
 * previous URL — ANO-079 one navigation later), and no same-path shallow adopt
 * — the `useUrlState` livelock `@SCN-049` catches that. Derived state, not a
 * ref, avoids a render-phase ref write; the extra pass is discarded before
 * commit, so committed renders see what the ref version gave.
 */
export function useCommittedLocation() {
  const currentLocation = useRouterState({ select: (state) => state.location });
  const isLoading = useRouterState({ select: (state) => state.isLoading });

  const [committed, setCommitted] = useState(currentLocation);
  if (
    committed !== currentLocation &&
    (!isLoading || currentLocation.pathname === committed.pathname)
  ) {
    setCommitted(currentLocation);
  }
  return committed;
}

/**
 * Whether the router has settled at least once.
 *
 * A latch, not a loading flag: Next assigned `isReady` true once and never
 * reset it (ANO-079), and its consumers rely on that — they hold work back
 * until the router first settles and capture the URL it settled on, rather
 * than re-arming on every subsequent navigation.
 */
export function useRouterHasSettled(): boolean {
  const isLoading = useRouterState({ select: (state) => state.isLoading });
  const [hasSettled, setHasSettled] = useState(false);
  if (!hasSettled && !isLoading) {
    setHasSettled(true);
  }
  return hasSettled;
}
